import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { isEmpty, noop } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import {
  ActionModalControlPanel,
  type ActionModalControlPanelProps,
  type Subsidiary,
} from '@revelio/core';
import type { Item } from '@revelio/iso-utility';

import {
  CompanySearchTree,
  type CompanySearchTreeHandle,
} from '../../../../shared/components/company-search-tree/company-search-tree';
import { useCompanyPlaceholderTree } from '../../../../shared/selection-lists/hooks/useCompanyPlaceholderTree';
import type { CompanyResultItem } from '../../../../shared/types/company-result-item';
import modalStyles from '../../../DataBuilder/data-builder-modals.module.css';
import panelStyles from './mapper/mapper-modal.module.css';

const sortByEmployeeCount = (a: Item, b: Item) =>
  Number(b.raw_emp_count ?? 0) - Number(a.raw_emp_count ?? 0);

export interface CompanySelectionsModalProps
  extends Partial<ActionModalControlPanelProps> {
  header?: string;
  isOpen: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onCancel?: () => void;
  submitHandler?: (selections: Subsidiary[]) => void;
  entityMenuHandlers?: {
    text: string;
    handler: (selections: Subsidiary[]) => void;
  }[];
  submitIsLoading?: boolean;
  setSubmitIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  treeHeight?: number;
  closeOnSubmit?: boolean;
  initialSelections?: Record<string, Subsidiary>;
  showPlaceholders?: boolean;
  allowEmptySubmit?: boolean;
}

export function CompanySelectionsModal({
  header = '',
  isOpen,
  onClose = noop,
  onCancel,
  submitHandler,
  entityMenuHandlers,
  submitIsLoading,
  submitText,
  treeHeight = 310,
  closeOnSubmit = true,
  initialSelections = {},
  showPlaceholders = false,
  allowEmptySubmit = false,
}: CompanySelectionsModalProps) {
  const [selections, setSelections] = useState<
    Record<string, CompanyResultItem>
  >(initialSelections as unknown as Record<string, CompanyResultItem>);

  const treeRef = useRef<CompanySearchTreeHandle>(null);

  const handleReset = () => {
    treeRef.current?.handleClearSelections();
  };

  const handleSubmit = () => {
    const value = Object.values(selections) as unknown as Subsidiary[];

    submitHandler?.(value);
    setSelections({});

    if (closeOnSubmit) {
      onClose();
    }
  };

  const adaptedMenuHandlers = entityMenuHandlers?.map((menuItem) => ({
    ...menuItem,
    handler: () =>
      menuItem.handler(Object.values(selections) as unknown as Subsidiary[]),
  }));

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelections(
      initialSelections as unknown as Record<string, CompanyResultItem>
    );
  }, [isOpen]);

  const placeholderListNodes = useCompanyPlaceholderTree({
    pause: !showPlaceholders,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent className={modalStyles.modalContent} minHeight="530px">
        <ModalHeader
          className={modalStyles.modalHeader}
          borderBottom="1px solid #E5EBF1"
          fontSize="17px"
          fontWeight="600"
          py="12px"
        >
          {header}
        </ModalHeader>
        <ModalCloseButton size="sm" />
        <ModalBody py="16px">
          <CompanySearchTree
            ref={treeRef}
            placeholderListNodes={placeholderListNodes}
            treeHeight={treeHeight}
            selections={selections}
            setSelections={setSelections}
            sortFn={sortByEmployeeCount}
            autoFocus
          />
        </ModalBody>
        <ModalFooter
          className={modalStyles.modalFooterHairline}
          borderTop="1px solid #E5EBF1"
        >
          <ActionModalControlPanel
            submitText={submitText}
            onClose={onClose}
            onCancel={onCancel}
            onSubmit={handleSubmit}
            menuHandlers={adaptedMenuHandlers}
            onReset={handleReset}
            submitIsDisabled={!allowEmptySubmit && isEmpty(selections)}
            submitIsLoading={submitIsLoading}
            customProps={{
              containerProps: {
                className: `${panelStyles.panel} ${panelStyles.panelReset}`,
              },
              submitButtonProps: {
                className: modalStyles.footerPrimaryButton,
              },
              cancelButtonProps: {
                className: modalStyles.footerCancelLink,
              },
            }}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
